var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import classNames from 'classnames';
import useTaroBaseEvents from '../_util/hooks/useTaroBaseEvents';
import useMergedState from '../_util/hooks/useMergedState';
import { createTaroFocusEvent, createTaroBlurEvent } from '../_util/taroEvent';
import calculateNodeHeight from './calculateNodeHeight';
import getSizingData from './getSizingData';
const Textarea = (_a, ref) => {
    var { style, className, name, value, placeholder, placeholderStyle: customPlaceholderStyle, placeholderClass = 'input-placeholder', disabled = false, maxlength = 140, focus, autoHeight, cursor, autoFocus, selectionStart = -1, selectionEnd = -1, onInput, onFocus, onBlur, 
    // unsupported props
    confirmType, confirmHold, fixed, cursorSpacing, showConfirmBar, adjustPosition } = _a, rest = __rest(_a, ["style", "className", "name", "value", "placeholder", "placeholderStyle", "placeholderClass", "disabled", "maxlength", "focus", "autoHeight", "cursor", "autoFocus", "selectionStart", "selectionEnd", "onInput", "onFocus", "onBlur", "confirmType", "confirmHold", "fixed", "cursorSpacing", "showConfirmBar", "adjustPosition"]);
    const props = useTaroBaseEvents(rest);
    const el = useRef(null);
    const textAreaEl = useRef(null);
    const placeholderEl = useRef(null);
    const keyCode = useRef(null);
    useImperativeHandle(ref, () => el.current);
    const [mergedValue, setMergedValue] = useMergedState('', {
        value
    });
    const mergedStyle = useMemo(() => {
        if (!textAreaEl.current) {
            return;
        }
        const sizingData = getSizingData(textAreaEl.current);
        if (!sizingData) {
            return;
        }
        const [height] = calculateNodeHeight(sizingData, mergedValue);
        if (!autoHeight || typeof height !== 'number') {
            return style;
        }
        const heightStyle = { height: `${height}px` };
        if (style) {
            return Object.assign(Object.assign({}, style), heightStyle);
        }
        return heightStyle;
    }, [mergedValue, autoHeight, style]);
    useEffect(() => {
        if (!placeholderEl.current) {
            return;
        }
        if (typeof customPlaceholderStyle === 'string') {
            placeholderEl.current.setAttribute('style', customPlaceholderStyle);
        }
        else {
            placeholderEl.current.setAttribute('style', '');
        }
    }, [customPlaceholderStyle]);
    useEffect(() => {
        if (!textAreaEl.current) {
            return;
        }
        if (focus) {
            textAreaEl.current.focus();
        }
        if (typeof cursor === 'number') {
            textAreaEl.current.setSelectionRange(cursor, cursor);
        }
        else if (typeof selectionStart === 'number' &&
            selectionStart !== -1 &&
            typeof selectionEnd === 'number' &&
            selectionEnd !== -1) {
            textAreaEl.current.setSelectionRange(selectionStart, selectionEnd);
        }
    }, []);
    const placeholderStyle = Object.assign({
        display: mergedValue ? 'none' : 'block'
    }, typeof customPlaceholderStyle === 'object'
        ? customPlaceholderStyle
        : undefined);
    return (_jsx("div", Object.assign({ ref: el, className: classNames('taro-textarea', className), style: mergedStyle }, props, { children: _jsxs("div", Object.assign({ className: 'taro-textarea__content' }, { children: [_jsx("div", Object.assign({ ref: placeholderEl, className: classNames('taro-textarea__placeholder', placeholderClass), style: placeholderStyle }, { children: placeholder })), _jsx("textarea", { ref: textAreaEl, className: 'taro-textarea__main', name: name, value: mergedValue, autoFocus: autoFocus, disabled: disabled, maxLength: maxlength === -1 ? undefined : maxlength, onInput: event => {
                        if (onInput) {
                            const { timeStamp, target, currentTarget } = event;
                            const el = event.target;
                            const taroEvent = {
                                currentTarget,
                                target,
                                detail: {
                                    cursor: el.selectionStart || 0,
                                    keyCode: keyCode.current,
                                    value: el.value
                                },
                                timeStamp,
                                type: 'input',
                                preventDefault: () => event.preventDefault(),
                                stopPropagation: () => event.stopPropagation()
                            };
                            onInput(taroEvent);
                        }
                    }, onChange: event => {
                        setMergedValue(event.target.value);
                    }, onFocus: event => {
                        if (onFocus) {
                            const taroEvent = createTaroFocusEvent(event);
                            onFocus(taroEvent);
                        }
                    }, onBlur: event => {
                        if (onBlur) {
                            const taroEvent = createTaroBlurEvent(event);
                            onBlur(taroEvent);
                        }
                    }, onKeyDown: event => {
                        keyCode.current = event.keyCode;
                    } })] })) })));
};
export default forwardRef(Textarea);
